import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getLimits, createLimit, updateLimit, deleteLimit, extractErrorMessage } from '../services/api';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import UserSelect from '../components/UserSelect';
import '../styles/Limits.css';

function Limits({ user }) {
  const toast = useToast();
  const [limits, setLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLimit, setEditingLimit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [newLimit, setNewLimit] = useState({
    user_id: '',
    category: 'soft_limit',
    name: '',
    description: '',
    level: 5,
    notes: ''
  });

  useEffect(() => {
    fetchLimits();
  }, []);

  const fetchLimits = async () => {
    try {
      setLoading(true);
      const data = await getLimits();
      setLimits(data.limits || data || []);
    } catch (error) {
      console.error('Error fetching limits:', error);
      toast.error(extractErrorMessage(error, 'Limits konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      if (editingLimit) {
        await updateLimit(editingLimit.id, newLimit);
      } else {
        await createLimit(newLimit);
      }
      setEditingLimit(null);
      setNewLimit({ user_id: '', category: 'soft_limit', name: '', description: '', level: 5, notes: '' });
      setShowModal(false);
      toast.success(editingLimit ? 'Limit gespeichert' : 'Limit erstellt');
      fetchLimits();
    } catch (error) {
      console.error('Error saving limit:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Speichern des Limits'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Limit wirklich löschen?')) return;
    try {
      await deleteLimit(id);
      toast.success('Limit gelöscht');
      fetchLimits();
    } catch (error) {
      console.error('Error deleting limit:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen des Limits'));
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const handleEdit = (limit) => {
    setEditingLimit(limit);
    setNewLimit({
      user_id: limit.user_id || '',
      category: limit.category,
      name: limit.name,
      description: limit.description || '',
      level: limit.level || 5,
      notes: limit.notes || ''
    });
    setShowModal(true);
  };

  const categories = [
    { value: 'hard_limit', label: 'Hartes Limit', color: '#f04747', bgColor: 'rgba(240, 71, 71, 0.15)' },
    { value: 'soft_limit', label: 'Weiches Limit', color: '#faa61a', bgColor: 'rgba(250, 166, 26, 0.15)' },
    { value: 'curious', label: 'Neugierig', color: '#43b581', bgColor: 'rgba(67, 181, 129, 0.15)' },
    { value: 'enthusiastic', label: 'Begeistert', color: '#7289da', bgColor: 'rgba(114, 137, 218, 0.15)' }
  ];

  const getCategoryStyle = (category) => {
    const cat = categories.find(c => c.value === category);
    if (!cat) return {};
    return { color: cat.color, backgroundColor: cat.bgColor, border: `1px solid ${cat.color}` };
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const filteredLimits = filterCategory === 'all'
    ? limits
    : limits.filter(l => l.category === filterCategory);

  const groupedLimits = categories.reduce((acc, cat) => {
    acc[cat.value] = filteredLimits.filter(l => l.category === cat.value);
    return acc;
  }, {});

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Limits</h1>
          <button className="add-btn" onClick={() => { setEditingLimit(null); setNewLimit({ user_id: '', category: 'soft_limit', name: '', description: '', level: 5, notes: '' }); setShowModal(true); }}>
            <FaPlus /> Neues Limit
          </button>
        </div>

        <div className="filter-tabs">
          <button className={filterCategory === 'all' ? 'active' : ''} onClick={() => setFilterCategory('all')}>Alle</button>
          {categories.map(cat => (
            <button
              key={cat.value}
              className={filterCategory === cat.value ? 'active' : ''}
              style={filterCategory === cat.value ? { background: cat.color, color: '#fff' } : {}}
              onClick={() => setFilterCategory(cat.value)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : filterCategory === 'all' ? (
          <div className="limits-grouped">
            {categories.map(cat => {
              const items = groupedLimits[cat.value] || [];
              if (items.length === 0) return null;
              return (
                <div key={cat.value} className="limits-group">
                  <h3 className="group-header" style={{ color: cat.color }}>
                    {cat.label} ({items.length})
                  </h3>
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Kategorie</th>
                          <th>Beschreibung</th>
                          <th>Level</th>
                          <th>Notizen</th>
                          <th>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(limit => (
                          <tr key={limit.id}>
                            <td><strong>{limit.name}</strong></td>
                            <td><span className="badge" style={getCategoryStyle(limit.category)}>{getCategoryLabel(limit.category)}</span></td>
                            <td>{limit.description || '-'}</td>
                            <td>{limit.level}/10</td>
                            <td>{limit.notes || '-'}</td>
                            <td className="actions-cell">
                              <button className="icon-btn edit" onClick={() => handleEdit(limit)} title="Bearbeiten"><FaEdit /></button>
                              <button className="icon-btn delete" onClick={() => handleDelete(limit.id)} title="Löschen"><FaTrash /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
            {filteredLimits.length === 0 && <div className="no-data">Keine Limits gefunden</div>}
          </div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kategorie</th>
                  <th>Beschreibung</th>
                  <th>Level</th>
                  <th>Notizen</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredLimits.length > 0 ? filteredLimits.map(limit => (
                  <tr key={limit.id}>
                    <td><strong>{limit.name}</strong></td>
                    <td><span className="badge" style={getCategoryStyle(limit.category)}>{getCategoryLabel(limit.category)}</span></td>
                    <td>{limit.description || '-'}</td>
                    <td>{limit.level}/10</td>
                    <td>{limit.notes || '-'}</td>
                    <td className="actions-cell">
                      <button className="icon-btn edit" onClick={() => handleEdit(limit)} title="Bearbeiten"><FaEdit /></button>
                      <button className="icon-btn delete" onClick={() => handleDelete(limit.id)} title="Löschen"><FaTrash /></button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="6" className="no-data">Keine Limits in dieser Kategorie</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>{editingLimit ? 'Limit bearbeiten' : 'Neues Limit'}</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <UserSelect
                    value={newLimit.user_id}
                    onChange={id => setNewLimit({...newLimit, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Kategorie *</label>
                  <select
                    value={newLimit.category}
                    onChange={e => setNewLimit({...newLimit, category: e.target.value})}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={newLimit.name}
                    onChange={e => setNewLimit({...newLimit, name: e.target.value})}
                    placeholder="Name des Limits"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={newLimit.description}
                    onChange={e => setNewLimit({...newLimit, description: e.target.value})}
                    placeholder="Beschreibung..."
                  />
                </div>
                <div className="form-group">
                  <label>Level: {newLimit.level}/10</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newLimit.level}
                    onChange={e => setNewLimit({...newLimit, level: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newLimit.notes}
                    onChange={e => setNewLimit({...newLimit, notes: e.target.value})}
                    placeholder="Weitere Notizen..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>
                    {saving ? 'Speichert...' : (editingLimit ? 'Speichern' : 'Erstellen')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Limits;
